// src/services/userService.js
// This file contains all the functions for interacting with the Supabase 'users' table.

// src/store/services/userService.js
import { supabase } from '../../supabaseClient';
import Compressor from 'compressorjs';

import { normalizeSearchText } from '../../utils/searchUtilsForAccounts';



export const getUserByIdForSecurity = async (userId) => {
    if (!userId) throw new Error("User ID is required");

    const { data, error } = await supabase
        .from('profiles')
        .select(`
            id,
            username,
            role,
            email,
            phone_number,
            profile_picture,
            user_rank,
            global_rank,
            strikes,
            location,
            loyalty,
            games_details,
            preferences,
            reputation,
            account_status,
            created_at,
            updated_at
        `)
        .eq('id', userId)
        .single();

    if (error) {
        console.error("Error fetching secure user profile:", error);
        throw error;
    }

    // Safely parse JSON fields if Supabase returns them as raw strings
    if (data && typeof data.reputation === 'string') {
        try {
            data.reputation = JSON.parse(data.reputation);
        } catch (e) {
            console.error("Error parsing reputation JSON:", e);
        }
    }

    return data;
};


export async function getUserMetrics(userId) {
    if (!userId) return null;

    try {
        // 1. Fetch profile reputation data and strikes
        const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('reputation, strikes')
            .eq('id', userId)
            .single();

        if (profileError) throw profileError;

        // --- DEEP-PARSING REPUTATION MATCHING ---
        let finalTier = 'New User';
        
        if (profileData?.reputation) {
            const repData = profileData.reputation;
            
            // Checks if current_tier is directly on the column object
            if (repData.current_tier) {
                finalTier = repData.current_tier;
            } 
            // Checks if it's nested inside a secondary 'reputation' key like your exact payload
            else if (repData.reputation && repData.reputation.current_tier) {
                finalTier = repData.reputation.current_tier;
            }
        }

        // [UPDATED]: Added food_orders to cross-platform transaction targets
        const tablesToAggregate = ['reservations', 'service_bookings', 'food_orders'];

        const queryPromises = tablesToAggregate.map(tableName => 
            supabase
                .from(tableName)
                .select('status')
                .eq('user_id', userId)
        );

        const results = await Promise.all(queryPromises);

        let totalReservationsCount = 0;
        let pickedUpReservations = 0;
        let cancelledReservationsByUser = 0;
        let noShowUps = 0;

        // 3. Flatten out and aggregate live status data
        results.forEach((res, index) => {
            const currentTable = tablesToAggregate[index];
            if (res.error) {
                console.error(`Error querying dynamic table [${currentTable}]:`, res.error.message);
                return;
            }

            if (res.data) {
                res.data.forEach(row => {
                    const status = row.status ? String(row.status).trim().toLowerCase() : '';
                    
                    if (!status) return;
                    
                    totalReservationsCount++;

                    if (['completed', 'picked_up', 'pickedup', 'showed_up', 'showedup', 'success', 'confirmed', 'completed_successfully'].includes(status)) {
                        pickedUpReservations++;
                    } else if (['cancelled', 'cancelled_by_user', 'canceled'].includes(status)) {
                        cancelledReservationsByUser++;
                    } else if (['no_show', 'no_show_up', 'noshow', 'absent'].includes(status)) {
                        noShowUps++;
                    }
                });
            }
        });

        return {
            current_tier: finalTier,
            strike_log: profileData?.strikes || { strike_1: { active: false }, strike_2: { active: false }, strike_3: { active: false } },
            total_reservations_count: totalReservationsCount,
            picked_up_reservations: pickedUpReservations,
            cancelled_reservations_by_user: cancelledReservationsByUser,
            no_show_ups: noShowUps
        };

    } catch (err) {
        console.error('Core failure in multi-table metrics aggregation:', err.message);
        return null;
    }
}

export const fetchUserById = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user by ID:', error);
    return null;
  }
  return data;
};

export const fetchUserByEmail = async (email) => {
  if (!email) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('email', email)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user by email:', error);
    return null;
  }
  return data;
};

export const updateUserDetails = async (userId, updates) => {
      const finalPayload = { ...updates };

    // Keep the accent-insensitive search column in sync whenever the name changes
    if (typeof finalPayload.username === 'string') {
        finalPayload.search_username_normalized = normalizeSearchText(finalPayload.username);
    }
  
    const { data, error } = await supabase
    .from('profiles')
    .update(finalPayload)
    .eq('id', userId)
    .select();

  if (error) {
    console.error('Error updating user details:', error);
    throw error;
  }
  return data ? data[0] : null;
};

// userService.js
export const updateUserLocation = async (userId, locationData) => {
  return await updateUserDetails(userId, { location: locationData });
};
/**
 * Fetches a user's data from the 'users' table using their username.
 * @param {string} username - The user's username.
 * @returns {Promise<object>} The user data object.
 */
export const fetchUserByUsername = async (username, locationData) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ location: locationData })
    .select('*')
    .eq('username', username)
    .single();

  if (error) {
    console.error('Error fetching user by username:', error);
    throw new Error('Failed to fetch user data by username.');
  }
  return data;
};


const compressImageAsync = (file) => {
    return new Promise((resolve, reject) => {
        new Compressor(file, {
            quality: 0.9,
            maxWidth: 1920,
            maxHeight: 1920,
            success(result) {
                resolve(new File([result], file.name, { type: file.type, lastModified: Date.now() }));
            },
            error(err) {
                reject(err);
            },
        });
    });
};

/**
 * Uploads a profile picture to ImageKit, logs it in tracked_images, and updates the profiles record.
 * @param {File} file - The uploaded avatar image file
 * @param {string} userId - The active user's ID
 */
export const uploadUserProfilePicture = async (file, userId) => {
    if (!file) return null;

    try {
        // 1. Compress file using Compressor.js
        const compressedFile = await compressImageAsync(file);

        // 2. Fetch credentials from Netlify Edge
        const authResponse = await fetch(`/.netlify/functions/auth-imagekit?type=profile_picture`);
        const authData = await authResponse.json();

        if (!authResponse.ok) throw new Error(authData.message || 'Failed to fetch ImageKit credentials.');

        const formData = new FormData();
        const publicKey = import.meta.env.VITE_IMAGEKIT_PUBLIC_KEY;
        
        // CLEAN FLAT FOLDER STRUCTURE FOR PROFILES
        const folderPath = `/porte17/users/profiles`;
        const uniqueId = self.crypto.randomUUID().split('-')[0];
        const fileName = `profile-${uniqueId}-${Date.now()}`;

        formData.append('file', compressedFile);
        formData.append('fileName', fileName);
        formData.append('publicKey', publicKey);
        formData.append('expire', authData.expire);
        formData.append('token', authData.token);
        formData.append('signature', authData.signature);
        formData.append('folder', folderPath);

        // 3. Upload to ImageKit
        const uploadResponse = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
            method: 'POST',
            body: formData,
        });

        const uploadData = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadData.message || 'ImageKit upload network failure.');

        // 4. Track full asset dimensions/meta in Supabase
        await supabase.from('tracked_images').insert([{
            file_id: uploadData.fileId,
            url: uploadData.url,
            file_path: uploadData.filePath,
            entity_type: 'user',
            image_type: 'profile_picture',
            uploaded_by: userId,
            shop_id: null, // Isolated user context
            width: uploadData.width || null,
            height: uploadData.height || null,
            file_size_bytes: compressedFile.size
        }]);

        // 5. Update the profiles data row with the direct public image URL
        const { error: profileError } = await supabase
            .from('profiles')
            .update({ profile_picture: uploadData.url })
            .eq('id', userId);

        if (profileError) throw profileError;

        return uploadData.url;
    } catch (error) {
        console.error('Profile photo update workflow failed:', error);
        throw error;
    }
};

// .............................................................

// Update user profile fields
export const updateProfileData = async ({ userId, updates }) => {
    const { data, error } = await supabase
        .from('profiles')
        .update({
            ...updates,
            updated_at: new Date().toISOString(),
        })
        .eq('id', userId)
        .select()
        .single();

    if (error) throw new Error(error.message);
    return data;
};


// Retrieve public/pro profile by its unique public_id, including joined shops
export const fetchProfileByPublicId = async (publicId) => {
    const { data, error } = await supabase
        .from('profiles')
        .select(`
            *,
            shop_memberships (
                role,
                shops (
                    id,
                    name,
                    slug,
                    shop_logo_url,
                    description,
                    shop_type
                )
            )
        `)
        .eq('public_id', publicId)
        .single();

    if (error) throw new Error(error.message);
    
    // Normalize data slightly to make it cleaner for the frontend to digest
    const shops = data.shop_memberships
        ? data.shop_memberships.map(m => ({ ...m.shops, membership_role: m.role }))
        : [];

    return {
        ...data,
        owned_shops: shops
    };
};// .............................................................

/**
 * Deletes a user from the 'users' table.
 * @param {string} userId - The user's Supabase ID.
 * @returns {Promise<boolean>} True if deletion was successful.
 */
export const deleteUserById = async (userId) => {
  const { error } = await supabase
    .from('profiles')
    .delete()
    .eq('id', userId);

  if (error) {
    console.error('Error deleting user:', error);
    throw new Error('Failed to delete user.');
  }
  return true;
};


// ---------------------Recalculating user-----------------------------


/**
 * Centrally recalculates user metrics across both reservations and bookings,
 * handles strict 30-day parameters for tiers, and preserves lifetime counts.
 */
export async function recalculateUserReputation(userId) {
    if (!userId) return null;

    try {
        // 1. Fetch user profile for active strikes tracking and strike logs
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('strikes, strike_log')
            .eq('id', userId)
            .single();

        if (profileError) throw profileError;

        let activeStrikes = 0;
        if (profile?.strikes) {
            if (profile.strikes.strike_1?.active) activeStrikes++;
            if (profile.strikes.strike_2?.active) activeStrikes++;
            if (profile.strikes.strike_3?.active) activeStrikes++;
        }

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // 2. Query both operational tables independently (Entire History)
        const [reservationsRes, bookingsRes] = await Promise.all([
            supabase.from('reservations').select('status, created_at').eq('user_id', userId),
            supabase.from('service_bookings').select('status, created_at').eq('user_id', userId)
        ]);

        if (reservationsRes.error) throw reservationsRes.error;
        if (bookingsRes.error) throw bookingsRes.error;

        // Cumulative metrics accumulators (Lifetime counters for the UI Profile row)
        let totalLifetime = 0;
        let completedLifetime = 0;
        let userCancelledLifetime = 0;
        let noShowsLifetime = 0;

        // 30-Day Window counters (Strictly for reputation tier evaluation parameters)
        let total30Days = 0;
        let userCancelled30Days = 0;
        let noShows30Days = 0;

        const unifiedAccountabilityTimeline = [];

        // 3. Process Reservations pool
        if (reservationsRes.data) {
            reservationsRes.data.forEach(item => {
                const status = item.status ? String(item.status).trim().toLowerCase() : '';
                const itemDate = new Date(item.created_at);
                const isWithin30Days = itemDate >= thirtyDaysAgo;

                // Explicit match for accountability metrics (ignoring confirmed, pending, rejected, shop-faults)
                if (['completed', 'picked_up', 'cancelled_by_user', 'no_show'].includes(status)) {
                    totalLifetime++;
                    if (isWithin30Days) total30Days++;

                    if (['completed', 'picked_up'].includes(status)) {
                        completedLifetime++;
                        unifiedAccountabilityTimeline.push({ status: 'completed', created_at: itemDate });
                    }
                    if (status === 'cancelled_by_user') {
                        userCancelledLifetime++;
                        if (isWithin30Days) userCancelled30Days++;
                        unifiedAccountabilityTimeline.push({ status: 'cancelled_by_user', created_at: itemDate });
                    }
                    if (status === 'no_show') {
                        noShowsLifetime++;
                        if (isWithin30Days) noShows30Days++;
                        unifiedAccountabilityTimeline.push({ status: 'no_show', created_at: itemDate });
                    }
                }
            });
        }

        // 4. Process Service Bookings pool
        if (bookingsRes.data) {
            bookingsRes.data.forEach(item => {
                const status = item.status ? String(item.status).trim().toLowerCase() : '';
                const itemDate = new Date(item.created_at);
                const isWithin30Days = itemDate >= thirtyDaysAgo;

                // Standardize service booking status naming variations
                if (['completed', 'canceled_by_user', 'canceled_user', 'no_show', 'no-show'].includes(status)) {
                    totalLifetime++;
                    if (isWithin30Days) total30Days++;

                    if (status === 'completed') {
                        completedLifetime++;
                        unifiedAccountabilityTimeline.push({ status: 'completed', created_at: itemDate });
                    }
                    if (['canceled_by_user', 'canceled_user'].includes(status)) {
                        userCancelledLifetime++;
                        if (isWithin30Days) userCancelled30Days++;
                        unifiedAccountabilityTimeline.push({ status: 'cancelled_by_user', created_at: itemDate });
                    }
                    if (['no_show', 'no-show'].includes(status)) {
                        noShowsLifetime++;
                        if (isWithin30Days) noShows30Days++;
                        unifiedAccountabilityTimeline.push({ status: 'no_show', created_at: itemDate });
                    }
                }
            });
        }

        // 5. Check Strict Rolling Last 5 Actions for Outstanding Tier Validation
        // Sort chronologically: newest interaction first
        unifiedAccountabilityTimeline.sort((a, b) => b.created_at - a.created_at);
        const rollingLast5 = unifiedAccountabilityTimeline.slice(0, 5);
        
        let isOutstandingCandidate = rollingLast5.length === 5;
        rollingLast5.forEach(item => {
            if (item.status !== 'completed') {
                isOutstandingCandidate = false;
            }
        });

        // 6. Tier Matrix Selection Rules Engine
        let computedTier = 'New User';
        const userCancellationRate = total30Days > 0 ? (userCancelled30Days / total30Days) : 0;

        if (activeStrikes > 0 || noShows30Days >= 2 || userCancellationRate > 0.4) {
            computedTier = 'Bad';
        } else if (isOutstandingCandidate && activeStrikes === 0) {
            computedTier = 'Outstanding';
        } else if (total30Days >= 5 && activeStrikes === 0) {
            computedTier = 'Great';
        } else if (total30Days >= 1 && total30Days <= 4 && activeStrikes === 0) {
            computedTier = 'Good';
        } else {
            computedTier = 'New User';
        }

        // 7. Save calculations back to the tables
        const reputationPayload = {
            current_tier: computedTier,
            updated_at: new Date().toISOString()
        };

        await Promise.all([
            // Write payload directly to profile meta block
            supabase.from('profiles').update({ reputation: reputationPayload }).eq('id', userId),
            // Upsert complete lifetime and strike logs numbers to user_metrics cache table
            supabase.from('user_metrics').upsert({
                user_id: userId,
                total_reservations_count: totalLifetime,
                picked_up_reservations: completedLifetime,
                cancelled_reservations_by_user: userCancelledLifetime, // Double 'll' standardized
                no_show_ups: noShowsLifetime,
                strike_log: profile?.strike_log || null // Pass strike logs forward cleanly for the frontend UI view
            }, { onConflict: 'user_id' })
        ]);

        return computedTier;

    } catch (err) {
        console.error('Calculations pipeline execution failure error:', err.message);
        return null;
    }
}